/**
 * Leaderboard service for luckyanimal-nawabari.
 *
 * - SDK method -> fetch fallback pattern.
 *   Native signed-in/session users are the exception: fetch is used directly to
 *   avoid reading `myRank` / `myScore` through a stale guest SDK session.
 * - In-memory cache with 30-second TTL.
 * - Cache invalidation after score submission via invalidateLeaderboardCache().
 *
 * 重要: Hub の生レスポンスは `myRank` / `myScore` (string) / `ymgUid` の
 * フラット形式で返ってくる。LeaderboardPanel が期待する `myEntry` は
 * `adaptLeaderboardResponse()` が組み立てる派生フィールド。
 */

import {
  getYmgSdk,
  getGameId,
  ymgFetch,
  isSdkAuthError,
  hasCapacitorSessionToken,
} from './sdk'
import { ApiError, notifyReauthRequired } from './ymgAuth'
import { getAuthState } from './auth'

/**
 * UI が直接消費する形に正規化した leaderboard データ。
 * Hub 生レスポンス (LeaderboardResponse) のフィールドを必要なものだけ
 * 取り出し、`myEntry` を組み立てる。
 */
export interface LeaderboardData {
  entries: LeaderboardEntry[]
  myEntry: { rank: number; score: number } | null
  ymgUid: string | null
}

interface CachedLeaderboard {
  data: LeaderboardData
  timestamp: number
}

const CACHE_TTL_MS = 30_000 // 30 seconds
const cache = new Map<string, CachedLeaderboard>()

function adaptLeaderboardResponse(raw: LeaderboardResponse): LeaderboardData {
  const myRank = typeof raw.myRank === 'number' ? raw.myRank : null
  // myScore はサーバーで string として来る（大数値対応）。number 化に失敗したら null。
  const parsedScore =
    typeof raw.myScore === 'string' && raw.myScore.length > 0
      ? Number(raw.myScore)
      : null
  const myScore =
    parsedScore !== null && Number.isFinite(parsedScore) ? parsedScore : null
  const myEntry =
    myRank !== null && myRank >= 0 && myScore !== null
      ? { rank: myRank + 1, score: myScore }
      : null
  return {
    entries: raw.entries ?? [],
    myEntry,
    ymgUid: raw.ymgUid ?? null,
  }
}

/**
 * Fetch the enriched leaderboard for the given period.
 * Returns cached data if within TTL.
 */
export async function fetchLeaderboard(
  period: 'daily' | 'weekly' | 'alltime',
  limit = 10,
): Promise<LeaderboardData> {
  const cacheKey = `${period}:${limit}`
  const cached = cache.get(cacheKey)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data
  }

  const gameId = getGameId()
  const preferFetch = getAuthState().status === 'signed_in' || (await hasCapacitorSessionToken())

  async function fetchDirect(): Promise<LeaderboardData> {
    const res = await ymgFetch<LeaderboardResponse>(
      `/games/${gameId}/leaderboards/${period}/enriched?limit=${limit}`,
      { method: 'GET' },
    )
    return adaptLeaderboardResponse(res)
  }

  if (preferFetch) {
    const data = await fetchDirect()
    cache.set(cacheKey, { data, timestamp: Date.now() })
    return data
  }

  try {
    const sdk = await getYmgSdk()
    if (typeof sdk.api?.getLeaderboardEnriched === 'function') {
      const res = await sdk.api.getLeaderboardEnriched({
        gameId,
        period,
        limit,
      })
      const data = adaptLeaderboardResponse(res)
      cache.set(cacheKey, { data, timestamp: Date.now() })
      return data
    }
  } catch (err) {
    // 401 はサイレントに fetch fallback に倒さず、再認証 UI に通知する。
    // 5xx / ネットワークエラーは fetch fallback で再試行する余地があるため
    // warn のみ残して fall through する。
    if (isSdkAuthError(err)) {
      const error =
        err instanceof ApiError
          ? err
          : new ApiError(401, { message: 'SDK leaderboard auth failed' }, undefined, true)
      notifyReauthRequired(error)
      throw error
    }
    console.warn('[Leaderboard] sdk method failed (non-auth), trying fetch:', err)
  }

  const data = await fetchDirect()
  cache.set(cacheKey, { data, timestamp: Date.now() })
  return data
}

/**
 * Clear all cached leaderboard data.
 * Call after score submission to force fresh data on next fetch.
 */
export function invalidateLeaderboardCache(): void {
  cache.clear()
}
